import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeRegisterPage() {
  const { inviteCode } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ first_name:"", last_name:"", phone:"", job_title:"", years_experience:0, classification:"" });
  // FIX-4 BUG-37: distingue 410 Gone (link scaduto) da 404/altri errori
  const [linkExpired, setLinkExpired] = useState(false);

  useEffect(() => {
    axios.get(`${API}/employee-register/${inviteCode}`)
      .then(r => {
        setInfo(r.data);
        // BUG-39: precompila nome/cognome se forniti da HR in fase di invito
        if (r.data?.first_name || r.data?.last_name) {
          setForm(p => ({ ...p, first_name: r.data.first_name || p.first_name, last_name: r.data.last_name || p.last_name }));
        }
      })
      .catch(e => {
        if (e.response?.status === 410) {
          setLinkExpired(true);
          setError("Link scaduto. Contatta il tuo HR per riceverne uno nuovo.");
        } else {
          setError(e.response?.data?.detail || "Link non valido o scaduto");
        }
      })
      .finally(() => setLoading(false));
  }, [inviteCode]);

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employee-register/${inviteCode}`, form);
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 410) {
        setLinkExpired(true);
        setError("Link scaduto. Contatta il tuo HR per riceverne uno nuovo.");
      } else {
        setError(err.response?.data?.detail || "Errore nella registrazione");
      }
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;

  // FIX-4 BUG-37: schermata dedicata link scaduto
  if (linkExpired) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]" data-testid="link-expired-page">
      <Card className="max-w-md w-full mx-4 border-red-200">
        <CardContent className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <span className="text-red-600 text-2xl">⏱</span>
          </div>
          <h2 className="text-xl font-semibold text-[#0D1F3C] mb-2">Link scaduto</h2>
          <p className="text-sm text-[#0D1F3C]/60">Contatta il tuo HR per riceverne uno nuovo. Gli inviti scadono dopo 15 giorni.</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]">
      <Card className="max-w-md w-full mx-4 border-[#0D1F3C]/8">
        <CardContent className="p-8 text-center">
          <CheckCircle size={48} weight="duotone" className="text-[#10B981] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0D1F3C] mb-2">Registrazione Completata</h2>
          <p className="text-sm text-[#0D1F3C]/60">Il tuo profilo e stato creato con successo. Il team HR ti contattera per i prossimi step.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB] p-6" data-testid="employee-register-page">
      <Card className="max-w-md w-full border-[#0D1F3C]/8">
        <CardContent className="p-8">
          <h1 className="text-2xl font-bold text-[#0D1F3C] mb-1">SELLY<span className="text-[#56A3F5]">®</span></h1>
          {info && <p className="text-sm text-[#0D1F3C]/50 mt-2 mb-4">{info.company_name} {info.department ? `— ${info.department}` : ''}</p>}
          <h2 className="text-lg font-medium text-[#0D1F3C] mb-4">Registrazione Dipendente</h2>
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Nome *</Label><Input data-testid="ereg-first-name" value={form.first_name} onChange={e=>up("first_name",e.target.value)} required /></div>
              <div><Label className="text-xs mb-1 block">Cognome *</Label><Input data-testid="ereg-last-name" value={form.last_name} onChange={e=>up("last_name",e.target.value)} required /></div>
            </div>
            <div><Label className="text-xs mb-1 block">Ruolo</Label><Input value={form.job_title} onChange={e=>up("job_title",e.target.value)} /></div>
            <div><Label className="text-xs mb-1 block">Telefono</Label><Input data-testid="ereg-phone" type="tel" value={form.phone} onChange={e=>up("phone",e.target.value)} placeholder="es. +39 333 1234567" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs mb-1 block">Anni di esperienza</Label><Input type="number" value={form.years_experience} onChange={e=>up("years_experience",parseInt(e.target.value)||0)} /></div>
              <div><Label className="text-xs mb-1 block">Inquadramento</Label><Input value={form.classification} onChange={e=>up("classification",e.target.value)} /></div>
            </div>
            <Button type="submit" data-testid="ereg-submit-btn" className="w-full h-11 bg-[#1B4FD8] hover:bg-[#0D1F3C]">Completa Registrazione</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
